import { OUTCOME_CLASS_LABELS } from '../constants/partner-data-gateway.constant';
import { FileProcessingStatus } from '../domain/enums';
import { ProviderRecentCounts } from '../domain/types';
import { IN_FLIGHT_STATUSES, OutcomeClass, OUTCOME_CLASSES } from '../types';

export const EMPTY_OUTCOME_COUNTS: ProviderRecentCounts = {
  completed: 0,
  partial: 0,
  failed: 0,
  rejected: 0,
  inFlight: 0,
};

/**
 * Which class a file status counts toward. Rejected is its own class, not a
 * failure: the gatekeeper rejects when no configuration matches, which is a
 * setup problem or noise rather than a pipeline fault.
 */
export const classifyOutcome = (
  status: FileProcessingStatus,
): OutcomeClass | null => {
  if (status === FileProcessingStatus.Completed) {
    return 'completed';
  }

  if (status === FileProcessingStatus.CompletedWithErrors) {
    return 'partial';
  }

  if (status === FileProcessingStatus.Failed) {
    return 'failed';
  }

  if (status === FileProcessingStatus.Rejected) {
    return 'rejected';
  }

  if (IN_FLIGHT_STATUSES.includes(status)) {
    return 'inFlight';
  }

  return null;
};

export const countOutcomes = (
  statuses: FileProcessingStatus[],
): ProviderRecentCounts =>
  statuses.reduce(
    (counts, status) => {
      const outcomeClass = classifyOutcome(status);

      if (outcomeClass === null) {
        return counts;
      }

      return { ...counts, [outcomeClass]: counts[outcomeClass] + 1 };
    },
    { ...EMPTY_OUTCOME_COUNTS },
  );

/** "7 completed, 1 failed" — zero classes omitted; empty when all are zero. */
export const describeOutcomeCounts = (counts: ProviderRecentCounts): string =>
  OUTCOME_CLASSES.filter(outcomeClass => counts[outcomeClass] > 0)
    .map(
      outcomeClass =>
        `${counts[outcomeClass]} ${OUTCOME_CLASS_LABELS[
          outcomeClass
        ].toLowerCase()}`,
    )
    .join(', ');
